/* eslint-disable clubmed/no-fake-component */
import type { ReactNode } from 'react';
import React, { forwardRef } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { BEFORE_APP } from 'Components/frameworks/portal/Portal.constants';
import { t } from 'Src/i18n';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import Portal from 'Components/frameworks/portal/Portal';
import { Text } from 'Components/frameworks/text/Text';
type Props = {
  children?: ReactNode;
  className?: string;
  color?: Colors;
  icon?: IconName;
  isFixed?: boolean;
  label?: string;
  onClose: () => unknown;
  portalName?: string;
  visible?: boolean;
};
export const Toast = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      className,
      color = COLORS.BLUE_WAVE,
      icon,
      isFixed = false,
      label,
      onClose,
      portalName = BEFORE_APP,
      visible,
      ...rest
    }: Props,
    ref,
  ) => {
    const { isAppMounted } = useResponsive();

    if (!isAppMounted) {
      return null;
    }

    return (
      <Portal name={portalName}>
        <div
          className={classnames(
            'end-0 top-0 isolate z-8 overflow-x-hidden py-20',
            {
              absolute: !isFixed,
              fixed: isFixed,
              'pointer-events-none': !visible,
              'px-20': visible,
            },
            className,
          )}
        >
          <div
            {...rest}
            aria-hidden={!visible}
            className={classnames('transform transition-all duration-250 ease-in', {
              'pointer-events-auto ltr:translate-x-full rtl:-translate-x-full': !visible,
              'translate-x-0': visible,
            })}
          >
            <div
              className={classnames('flex rounded-12 bg-white font-bold', `text-${color}`, {
                'shadow-md': visible,
              })}
              data-testid="Toast"
              ref={ref}
            >
              <div className="flex flex-1 items-center gap-5 p-10">
                <Icon className="shrink-0" name={icon} width="1.875rem" />
                {label && <Text className="flex-1" text={t(label)} />}
                {children}
              </div>
              <button
                aria-disabled={!visible}
                className="shrink-0 border-s-1 border-lightGrey p-10 text-black transition-colors duration-250 ease-in hocus:text-black-active"
                onClick={onClose}
                tabIndex={visible ? 0 : -1}
                type="button"
              >
                <Icon name={ICONS.CROSS} width="0.625rem" />
              </button>
            </div>
          </div>
        </div>
      </Portal>
    );
  },
);
